"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { IMuxVideoPlayer } from "@/sanity/globals/VideoPlayer";
import { richTextComponent } from "@/sanity/sanityPortableText";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shadcn/ui/collapsible";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/ui/dialog";
import { SanityVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import UnlockDialog from "./platformDemo/UnlockDialog";

// ── Types ────────────────────────────────────────────────────────────────────
interface ICourseLesson {
  title?: string;
  duration?: string;
  thumbnail?: GROQImage;
  locked?: boolean;
}

interface ICourseModule {
  label?: string;
  lessons?: ICourseLesson[];
}

export interface ICourseDetail {
  name?: string;
  author?: string;
  avatar?: GROQImage;
  /** Branded course title artwork (svg/png URL) — shown over the banner (desktop). */
  titleImage?: string;
  /** Pre-designed course banner (the branded graphic + intro still). */
  banner?: GROQImage;
  /** Mobile crop of the same banner. Falls back to `banner`. */
  bannerMobile?: GROQImage;
  description?: PortableTextBlock[];
  videosLabel?: string;
  duration?: string;
  watchIntroUrl?: string;
  /** Mux playback id of the course's intro (first lesson) — opens the player. */
  introPlaybackId?: string;
  modules?: ICourseModule[];
}

interface CourseListSectionProps {
  courses?: ICourseDetail[];
  /** Unlock popup copy (Sanity-editable, per this section). */
  unlockHeading?: string;
  unlockBody?: string;
  unlockCta?: GROQButton;
}

// ── Icons ────────────────────────────────────────────────────────────────────
const ChevronDown = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    aria-hidden
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlayFilled = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M7 5v14l11-7L7 5z" />
  </svg>
);

const MediaLibrary = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <rect width="20" height="20" fill="white" fillOpacity="0.01" />
    <path d="M8.125 9.375V14.375L12.5 11.875L8.125 9.375Z" fill="#98D8FF" />
    <path
      d="M16.25 17.5H3.75C3.41859 17.4997 3.10085 17.3678 2.8665 17.1335C2.63216 16.8992 2.50035 16.5814 2.5 16.25V7.5C2.50035 7.16859 2.63216 6.85085 2.8665 6.6165C3.10085 6.38215 3.41859 6.25035 3.75 6.25H16.25C16.5814 6.25035 16.8992 6.38215 17.1335 6.6165C17.3678 6.85085 17.4997 7.16859 17.5 7.5V16.25C17.4997 16.5814 17.3678 16.8992 17.1335 17.1335C16.8992 17.3678 16.5814 17.4997 16.25 17.5ZM3.75 7.5V16.25H16.25V7.5H3.75Z"
      fill="#98D8FF"
    />
    <path d="M16.25 3.75H3.75V5H16.25V3.75Z" fill="#98D8FF" />
    <path d="M15 1.25H5V2.5H15V1.25Z" fill="#98D8FF" />
  </svg>
);

const TimeIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <rect width="20" height="20" fill="white" fillOpacity="0.01" />
    <path
      d="M10 18.75C8.26942 18.75 6.57769 18.2368 5.13876 17.2754C3.69983 16.3139 2.57832 14.9473 1.91606 13.3485C1.25379 11.7496 1.08051 9.9903 1.41813 8.29296C1.75575 6.59563 2.58911 5.03653 3.81282 3.81282C5.03653 2.58911 6.59563 1.75575 8.29296 1.41813C9.9903 1.08051 11.7496 1.25379 13.3485 1.91606C14.9473 2.57832 16.3139 3.69983 17.2754 5.13876C18.2368 6.57769 18.75 8.26942 18.75 10C18.75 12.3206 17.8281 14.5462 16.1872 16.1872C14.5462 17.8281 12.3206 18.75 10 18.75ZM10 2.5C8.51664 2.5 7.0666 2.93987 5.83323 3.76398C4.59986 4.58809 3.63856 5.75943 3.07091 7.12988C2.50325 8.50032 2.35473 10.0083 2.64411 11.4632C2.9335 12.918 3.64781 14.2544 4.6967 15.3033C5.7456 16.3522 7.08197 17.0665 8.53683 17.3559C9.99168 17.6453 11.4997 17.4968 12.8701 16.9291C14.2406 16.3614 15.4119 15.4002 16.236 14.1668C17.0601 12.9334 17.5 11.4834 17.5 10C17.5 8.01088 16.7098 6.10323 15.3033 4.6967C13.8968 3.29018 11.9891 2.5 10 2.5Z"
      fill="#98D8FF"
    />
    <path
      d="M12.8688 13.75L9.375 10.2563V4.375H10.625V9.7375L13.75 12.8688L12.8688 13.75Z"
      fill="#98D8FF"
    />
  </svg>
);

const LockIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    className={className}
    aria-hidden
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="8.5"
      width="12"
      height="8"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M6.5 8.5V6.5a3.5 3.5 0 117 0v2"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

// ── Lesson row ─────────────────────────────────────────────────────────────
// Mobile design (Figma 1935:908): number + small thumb + stacked title/duration.
// Only the highlighted row (usually the first in the module) gets the subtle
// `bg-white/[0.06]` background — a "next up" affordance.
function LessonRow({
  lesson,
  index,
  highlighted,
  onClick,
}: {
  lesson: ICourseLesson;
  index: number;
  highlighted?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center rounded-[0.875rem] cursor-pointer transition-colors duration-300 gap-[2rem] px-[1.5rem] py-[1.75rem] lg:pl-[2.5rem] lg:pr-[3.5rem] lg:py-[1.5rem] lg:gap-[3rem] ${
        highlighted ? "bg-white/[0.06]" : "hover:bg-white/[0.06]"
      }`}
    >
      <span className="shrink-0 font-geist font-medium tabular-nums text-white/70 text-[2.25rem] leading-[1.4]">
        {String(index + 1).padStart(2, "0")}
      </span>
      <div className="overflow-hidden relative shrink-0 rounded-[0.75rem] border border-white/[0.08] w-[16.625rem] h-[9.375rem] lg:w-[26.125rem] lg:h-[15.625rem]">
        {lesson.thumbnail && (
          <Image
            src={lesson.thumbnail}
            alt={lesson.title ?? ""}
            fill
            sizes="(max-width: 992px) 16.625rem, 26.125rem"
            className="object-cover"
          />
        )}
        {lesson.locked && (
          <div className="flex absolute inset-0 justify-center items-center backdrop-blur-[0.9rem] bg-black/20">
            <span className="flex justify-center items-center text-white rounded-full border border-white/10 bg-white/10 p-[1.25rem] lg:p-[1.75rem]">
              <LockIcon className="w-[1.75rem] h-[1.75rem] lg:w-[2.5rem] lg:h-[2.5rem]" />
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-1 min-w-0 flex-col justify-center gap-[1.5rem] lg:flex-row lg:items-center lg:justify-between lg:gap-[3rem]">
        <p className="m-0 font-geist font-medium text-white text-[2.25rem] leading-[1.24] lg:leading-[1.4]">
          {lesson.title}
        </p>
        {lesson.duration && (
          <span className="shrink-0 font-geist text-white/50 text-[1.75rem] leading-normal lg:font-medium lg:text-[2.25rem] lg:leading-[1.4] lg:text-white/40">
            {lesson.duration}
          </span>
        )}
      </div>
    </div>
  );
}

// ── A single course (collapsible) ─────────────────────────────────────────────
function CourseItem({
  course,
  defaultOpen,
  onWatchIntro,
  onLocked,
}: {
  course: ICourseDetail;
  defaultOpen?: boolean;
  onWatchIntro: (playbackId: string) => void;
  onLocked: () => void;
}) {
  const [activeModule, setActiveModule] = useState(0);
  const modules = course.modules ?? [];
  const lessons = modules[activeModule]?.lessons ?? [];
  const bannerMobile = course.bannerMobile ?? course.banner;

  return (
    <Collapsible
      defaultOpen={defaultOpen}
      className="flex flex-col gap-[0.875rem]"
    >
      {/* Trigger — avatar + name + author + chevron */}
      <CollapsibleTrigger asChild>
        <button className="flex justify-between items-center w-full text-left rounded-[1.5rem] border border-white/10 bg-white/[0.08] px-[2.25rem] py-[1.5rem] gap-[2rem] group">
          <div className="flex items-center min-w-0 gap-[1.75rem]">
            <div className="overflow-hidden relative shrink-0 rounded-full bg-white/10 w-[4.5rem] lg:w-[5.375rem]">
              {course.avatar && (
                <Image
                  src={course.avatar}
                  alt={course.author ?? ""}
                  className="!w-full object-cover"
                />
              )}
            </div>
            <span className="font-news tracking-[-0.055rem] text-white text-[2.2rem] lg:text-[3rem] truncate">
              {course.name}
            </span>
          </div>
          <div className="flex items-center shrink-0 gap-[2rem]">
            {course.author && (
              <span className="hidden font-news tracking-[-0.055rem] text-white/60 sm:inline text-[2.2rem] lg:text-[3rem] whitespace-nowrap">
                <em className="italic font-news">{course.author}</em>
              </span>
            )}
            <ChevronDown className="shrink-0 text-white transition-transform duration-300 w-[3rem] h-[3rem] group-data-[state=open]:rotate-180" />
          </div>
        </button>
      </CollapsibleTrigger>

      {/* Content — banner, meta, lessons */}
      <CollapsibleContent className="overflow-hidden">
        <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.08] px-[2rem] py-[2.5rem] lg:px-[3.5rem] lg:py-[3.75rem]">
          <div className="flex flex-col gap-[5rem]">
            <div className="flex flex-col gap-[3rem] lg:gap-[4.25rem]">
              {/* Banner (image) with the Watch intro overlay */}
              <div className="overflow-hidden relative w-full rounded-[1rem] bg-white/[0.04] h-[27.125rem] lg:h-[53.5rem]">
                <div className="absolute left-0 top-0 h-full w-[40rem] bg-gradient-to-r from-black/40 to-transparent z-[1]" />
                {course.banner && (
                  <Image
                    src={course.banner}
                    alt={course.name ?? ""}
                    fill
                    sizes="(max-width: 992px) 100vw, 142.25rem"
                    className="hidden object-cover lg:block"
                  />
                )}
                {bannerMobile && (
                  <Image
                    src={bannerMobile}
                    alt={course.name ?? ""}
                    fill
                    sizes="!w-[45.75rem] !h-[27.125rem]"
                    className="object-cover lg:hidden"
                  />
                )}

                {/* Branded title artwork — desktop only, top-left over the banner. */}
                {course.titleImage && (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={course.titleImage}
                    alt={course.name ?? ""}
                    className="hidden lg:block absolute z-[2] left-[5rem] top-[4rem] h-auto w-auto max-w-[48rem] max-h-[16rem] object-contain object-left-top drop-shadow-[0_0.4rem_1.2rem_rgba(0,0,0,0.45)] pointer-events-none"
                  />
                )}

                {course.introPlaybackId ? (
                  <button
                    type="button"
                    onClick={() => onWatchIntro(course.introPlaybackId!)}
                    className="flex absolute z-[2] items-center bg-white rounded-[1rem] gap-[1rem] px-[2rem] py-[1.4rem] lg:px-[3rem] lg:py-[2rem] left-[2rem] bottom-[2rem] lg:left-[5rem] lg:bottom-[3rem] transition-transform hover:scale-[1.02]"
                  >
                    <PlayFilled className="text-black w-[2.5rem] h-[2.5rem] lg:w-[3rem] lg:h-[3rem]" />
                    <span className="font-geist font-semibold tracking-[-0.05rem] text-black text-[2rem] lg:text-[2.5rem]">
                      Watch intro
                    </span>
                  </button>
                ) : course.watchIntroUrl ? (
                  <a
                    href={course.watchIntroUrl}
                    className="flex absolute items-center bg-white rounded-[1rem] gap-[1rem] px-[2rem] py-[1.4rem] lg:px-[3rem] lg:py-[2rem] left-[2rem] bottom-[2rem] lg:left-[5rem] lg:bottom-[3rem]"
                  >
                    <PlayFilled className="text-black w-[2.5rem] h-[2.5rem] lg:w-[3rem] lg:h-[3rem]" />
                    <span className="font-geist font-semibold tracking-[-0.05rem] text-black text-[2rem] lg:text-[2.5rem]">
                      Watch intro
                    </span>
                  </a>
                ) : null}
              </div>

              {/* Description + meta */}
              {(course.description?.length ||
                course.videosLabel ||
                course.duration) && (
                <div className="flex flex-col-reverse justify-between px-[1.5rem] gap-[2rem] lg:flex-row lg:items-start lg:gap-[3rem]">
                  {course.description?.length ? (
                    <div className="richtext richtext-start prose-p:m-0 prose-p:font-geist prose-p:leading-[1.5] prose-p:text-white/80 prose-p:text-[1.8rem] lg:prose-p:text-[2.25rem] lg:w-[102.75rem]">
                      <PortableText
                        value={course.description}
                        components={richTextComponent}
                      />
                    </div>
                  ) : null}
                  <div className="flex items-center shrink-0 gap-[3rem]">
                    {course.videosLabel && (
                      <div className="flex items-center text-white gap-[0.8rem]">
                        <MediaLibrary className="opacity-70 w-[2.5rem] h-[2.5rem]" />
                        <span className="font-geist font-medium text-[1.8rem] lg:text-[2rem] text-white/70 whitespace-nowrap">
                          {course.videosLabel}
                        </span>
                      </div>
                    )}
                    {course.videosLabel && course.duration && (
                      <div className="self-stretch w-px bg-white/20" />
                    )}
                    {course.duration && (
                      <div className="flex items-center text-white gap-[0.8rem]">
                        <TimeIcon className="opacity-70 w-[2.5rem] h-[2.5rem]" />
                        <span className="font-geist font-medium text-[1.8rem] lg:text-[2rem] text-white/70 whitespace-nowrap">
                          {course.duration}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* All classes */}
            {modules.length > 0 && (
              <div className="flex flex-col lg:px-[1.5rem] gap-[4rem]">
                <div className="w-full h-px bg-white/10" />
                <div className="flex flex-col gap-[2rem] lg:gap-[3.5rem]">
                  <p className="m-0 font-news tracking-[-0.055rem] text-white text-[3rem]">
                    All classes
                  </p>

                  {/* Module tabs — one horizontally-scrollable row so long
                      module titles never wrap into multiple lines. Uses generic
                      "Module NN" labels per the Figma design. */}
                  {modules.length > 1 ? (
                    <div className="flex items-center overflow-x-auto gap-[1rem] pb-[0.25rem] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {modules.map((mod, mi) => (
                        <button
                          key={mi}
                          type="button"
                          onClick={() => setActiveModule(mi)}
                          className={`shrink-0 whitespace-nowrap px-[1rem] py-[1.5rem] rounded-t-[0.75rem] font-geist text-[2rem] transition-colors ${
                            mi === activeModule
                              ? "border-b border-[#98d8ff] text-white/80"
                              : "text-white/40 hover:text-white/70"
                          }`}
                        >
                          {`Module ${String(mi + 1).padStart(2, "0")}`}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {/* Lessons — the intro (unlocked) opens the player; the rest
                      open the unlock popup. */}
                  <div className="flex flex-col">
                    {lessons.map((lesson, li) => (
                      <LessonRow
                        key={li}
                        lesson={lesson}
                        index={li}
                        highlighted={li === 0}
                        onClick={() =>
                          !lesson.locked && course.introPlaybackId
                            ? onWatchIntro(course.introPlaybackId)
                            : onLocked()
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Fullscreen-ish modal that plays a course intro (Mux) via the shared vidstack
// player. Controlled by the active playback id; `[&>button]:hidden` drops the
// dark default close so we can show a light one over the video.
function IntroModal({
  playbackId,
  onClose,
}: {
  playbackId: string | null;
  onClose: () => void;
}) {
  const playerData = playbackId
    ? ({
        embed: false,
        muxVideo: { playbackId, assetId: "", filename: "", status: "ready" },
        embedUrl: "",
        thumbnail: null,
      } as IMuxVideoPlayer)
    : null;

  return (
    <Dialog open={!!playbackId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!w-[min(150rem,calc(100%-4rem))] overflow-hidden rounded-[1rem] border border-white/10 bg-black p-0 [&>button]:hidden">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Course intro</DialogTitle>
            <DialogDescription>Intro video preview</DialogDescription>
          </DialogHeader>
        </VisuallyHidden>

        <div className="relative w-full bg-black aspect-video">
          {playerData && (
            <SanityVideoPlayer
              playerData={playerData}
              autoPlay
              className="w-full h-full"
            />
          )}
        </div>

        <DialogClose className="absolute right-[1.5rem] top-[1.5rem] z-20 flex items-center justify-center rounded-full bg-black/50 text-white/90 backdrop-blur p-[1rem] transition-opacity hover:opacity-100 focus:outline-none">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            className="w-[2.5rem] h-[2.5rem]"
            aria-hidden
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.0606 12L19.5 5.56058L18.4394 4.5L12 10.9394L5.56072 4.5L4.5 5.56058L10.9394 12L4.5 18.4394L5.56072 19.5L12 13.0606L18.4394 19.5L19.5 18.4394L13.0606 12Z"
              fill="currentColor"
            />
          </svg>
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}

export default function CourseListSection({
  courses,
  unlockHeading,
  unlockBody,
  unlockCta,
}: CourseListSectionProps) {
  // SSR with the (marketing) courses; upgrade to the live platform catalogue.
  const [list, setList] = useState<ICourseDetail[]>(courses ?? []);
  const [activeIntro, setActiveIntro] = useState<string | null>(null);
  const [unlockOpen, setUnlockOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/platform-courses")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: ICourseDetail[] | null) => {
        if (!cancelled && Array.isArray(data) && data.length) setList(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (list.length === 0) return null;

  return (
    <section className="w-full bg-black pb-[8rem] lg:pb-[17.5rem]">
      <div className="flex flex-col mx-auto w-[min(150rem,100%)] px-[2rem] lg:px-0 gap-[1rem]">
        {list.map((course, i) => (
          <CourseItem
            key={i}
            course={course}
            defaultOpen={i === 0}
            onWatchIntro={setActiveIntro}
            onLocked={() => setUnlockOpen(true)}
          />
        ))}
      </div>

      <IntroModal
        playbackId={activeIntro}
        onClose={() => setActiveIntro(null)}
      />

      <UnlockDialog
        open={unlockOpen}
        onOpenChange={setUnlockOpen}
        heading={unlockHeading || "Unlock every lesson"}
        body={
          unlockBody ||
          "Register now for free to unlock the full course library — hours of masterclasses from Hedge Fund Traders, World Trading Champions & Market Makers."
        }
        ctaLabel={unlockCta?.cta?.title}
        cta={unlockCta}
      />
    </section>
  );
}
